import { trackAnalyticsEvent } from '@autone/analytics';
import { useAuthorization } from '@autone/auth';
import { useAutoneTranslation } from '@autone/translations';
import {
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellTitle,
  type DataTableLoadingObject,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';
import { useCallback } from 'react';

import { type APP_NAME } from '../../../../consts';
import {
  useEditAssortmentSkuLocationMutation,
  useGetAssortmentCustomerConfigQuery,
} from '../../../../redux/assortment';
import { type TransformedAssortmentSkuLocationsItem } from '../../../../redux/assortment/schemas/get-assortment-sku-locations.schema';
import { AssortmentSwitch } from '../../components/AssortmentSwitch';

const COLUMN_ID = 'current_assortment' as const;

export type CurrentAssortmentColumnId = typeof COLUMN_ID;

interface CurrentAssortmentFields {
  sku: TransformedAssortmentSkuLocationsItem['sku'];
  location: TransformedAssortmentSkuLocationsItem['location'];
  is_assorted: TransformedAssortmentSkuLocationsItem['is_assorted'];
  is_managed_by_user: TransformedAssortmentSkuLocationsItem['is_managed_by_user'];
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<CurrentAssortmentFields>();

export const createCurrentAssortmentColumn = <
  T extends CurrentAssortmentFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.is_assorted),
    {
      id: COLUMN_ID,
      size: 190,
      header: function CurrentAssortmentHeader() {
        const { t } = useAutoneTranslation<typeof APP_NAME>();

        return (
          <DataTableCellContent align="left">
            <DataTableCellTitle className="uppercase font-medium whitespace-normal text-center">
              {t('table-columns.current-assortment')}
            </DataTableCellTitle>
          </DataTableCellContent>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function CurrentAssortmentCell({ row }) {
            const { is_assorted, sku, location, is_managed_by_user } =
              row.original;
            const skuId = sku.id;
            const locationId = location.id;

            const { hasReadOnlyAccess } = useAuthorization();

            const [editSkuLocationTrigger] =
              useEditAssortmentSkuLocationMutation();
            const { data, isLoading: configIsLoading } =
              useGetAssortmentCustomerConfigQuery({});
            const { use_data_feed_for_assortment } = data ?? {};

            const handleEditSkuLocationAssortedStatus = useCallback(
              (checked: boolean) => {
                trackAnalyticsEvent({
                  category: 'assortment_tables',
                  action: 'sku_locations_table:assortment_updated',
                  properties: {
                    sku_id: skuId,
                    location_id: locationId,
                    is_assorted: checked,
                  },
                });

                editSkuLocationTrigger({
                  path: { sku_id: skuId, location_id: locationId },
                  body: {
                    assortment_update: {
                      update_type: 'is_assorted',
                      new_value: checked,
                    },
                  },
                });
              },
              [skuId, locationId, editSkuLocationTrigger],
            );

            const isDisabled =
              is_assorted.awaiting_requests > 0 ||
              is_assorted.is_disabled ||
              hasReadOnlyAccess ||
              configIsLoading ||
              use_data_feed_for_assortment === true ||
              use_data_feed_for_assortment === undefined;

            return (
              <DataTableCellContent align="left">
                <AssortmentSwitch
                  isDisabled={isDisabled}
                  isAssorted={is_assorted.value}
                  isAutopilot={!is_managed_by_user}
                  onChange={handleEditSkuLocationAssortedStatus}
                />
              </DataTableCellContent>
            );
          },
        }),
    },
  );
